import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Upload, Loader2, Save, ShoppingCart, ArrowLeft, Sun, Droplets, Mountain, Sparkles, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { identifyPlant, savePlant, addShoppingItem } from "@/lib/api";

export default function IdentifyPage({ deviceId }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImagePreview(ev.target.result);
      const b64 = ev.target.result.split(",")[1];
      setImageBase64(b64);
      setResult(null);
      setSaved(false);
    };
    reader.readAsDataURL(file);
  };

  const handleIdentify = async () => {
    if (!imageBase64) return;
    setLoading(true);
    try {
      const data = await identifyPlant(imageBase64, deviceId);
      setResult(data);
    } catch (err) {
      toast.error("Could not identify the plant. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!result) return;
    try {
      await savePlant({
        device_id: deviceId,
        name: result.name,
        scientific_name: result.scientific_name || "",
        care_summary: result.care_summary || "",
        sunlight: result.sunlight || "",
        water: result.water || "",
        soil: result.soil || "",
        image_path: result.image_path || "",
        full_details: JSON.stringify(result),
      });
      setSaved(true);
      toast.success("Plant saved to your collection!");
    } catch (err) {
      toast.error("Could not save plant.");
    }
  };

  const handleAddToList = async (productName) => {
    try {
      await addShoppingItem({
        device_id: deviceId,
        name: productName,
        category: "suggested",
      });
      toast.success(`Added "${productName}" to your shopping list`);
    } catch (err) {
      toast.error("Could not add to list.");
    }
  };

  return (
    <div className="px-5 pt-6 pb-4" data-testid="identify-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} data-testid="identify-back-btn" className="w-12 h-12 rounded-xl border-2 border-[#0A2918] flex items-center justify-center press-active hard-shadow-sm bg-white">
          <ArrowLeft size={24} strokeWidth={2.5} />
        </button>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0A2918]" style={{ fontFamily: 'Work Sans' }}>
          Identify a Plant
        </h1>
      </div>

      {/* Camera / Upload Area */}
      {!imagePreview && (
        <div className="space-y-4">
          <div
            onClick={() => fileInputRef.current?.click()}
            data-testid="photo-upload-area"
            className="bg-[#F2F7F4] border-2 border-dashed border-[#0A2918] rounded-2xl p-8 flex flex-col items-center justify-center gap-4 cursor-pointer min-h-[250px] press-active transition-colors hover:bg-[#E8EFEA]"
          >
            <Camera size={48} strokeWidth={2} className="text-[#28593A]" />
            <p className="text-lg font-bold text-[#0A2918] text-center">Tap to take a photo or upload</p>
            <p className="text-base text-[#2A4D3B] text-center">Point your camera at any plant to get started</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleImageSelect}
            className="hidden"
            data-testid="photo-input"
          />
        </div>
      )}

      {/* Image Preview */}
      {imagePreview && !result && (
        <div className="space-y-4">
          <div className="rounded-2xl overflow-hidden border-2 border-[#0A2918] hard-shadow">
            <img src={imagePreview} alt="Plant" className="w-full h-64 object-cover" data-testid="photo-preview" />
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => { setImagePreview(null); setImageBase64(null); }}
              variant="outline"
              data-testid="retake-btn"
              className="flex-1 h-14 text-lg font-bold border-2 border-[#0A2918] rounded-xl press-active hard-shadow-sm"
            >
              Retake
            </Button>
            <Button
              onClick={handleIdentify}
              disabled={loading}
              data-testid="identify-btn"
              className="flex-1 h-14 text-lg font-bold bg-[#0A2918] text-white rounded-xl press-active hard-shadow"
            >
              {loading ? (
                <span className="flex items-center gap-2"><Loader2 className="animate-spin" size={22} /> Analyzing...</span>
              ) : (
                <span className="flex items-center gap-2"><Sparkles size={22} strokeWidth={2.5} /> Identify</span>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="mt-6 bg-[#F2F7F4] rounded-2xl p-8 border-2 border-[#0A2918] flex flex-col items-center gap-3 animate-gentle-pulse">
          <Loader2 className="animate-spin text-[#28593A]" size={40} />
          <p className="text-lg font-bold text-[#0A2918]">Analyzing your plant...</p>
          <p className="text-base text-[#2A4D3B]">This may take a moment</p>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-4 animate-slide-up" data-testid="identify-result">
          {/* Plant Image */}
          {imagePreview && (
            <div className="rounded-2xl overflow-hidden border-2 border-[#0A2918] hard-shadow">
              <img src={imagePreview} alt={result.name} className="w-full h-48 object-cover" />
            </div>
          )}

          {/* Plant Name Card */}
          <div className="bg-white border-2 border-[#0A2918] rounded-2xl p-5 hard-shadow" data-testid="plant-name-card">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold text-[#0A2918]" style={{ fontFamily: 'Work Sans' }}>{result.name}</h2>
                {result.scientific_name && (
                  <p className="text-base italic text-[#2A4D3B] mt-1">{result.scientific_name}</p>
                )}
              </div>
              {result.confidence && (
                <span className={`text-sm font-bold px-3 py-1 rounded-full ${
                  result.confidence === 'high' ? 'bg-green-100 text-green-800' :
                  result.confidence === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {result.confidence} confidence
                </span>
              )}
            </div>
            {result.care_summary && (
              <p className="text-base text-[#2A4D3B] mt-3 leading-relaxed">{result.care_summary}</p>
            )}
          </div>

          {/* Care Info Grid */}
          <div className="grid grid-cols-3 gap-3">
            {result.sunlight && (
              <div className="bg-[#F2F7F4] border-2 border-[#0A2918] rounded-xl p-3 flex flex-col items-center text-center hard-shadow-sm">
                <Sun size={24} strokeWidth={2.5} className="text-[#E25F45] mb-1" />
                <span className="text-xs font-bold text-[#0A2918] uppercase">Sun</span>
                <span className="text-sm text-[#2A4D3B] mt-1">{result.sunlight}</span>
              </div>
            )}
            {result.water && (
              <div className="bg-[#F2F7F4] border-2 border-[#0A2918] rounded-xl p-3 flex flex-col items-center text-center hard-shadow-sm">
                <Droplets size={24} strokeWidth={2.5} className="text-blue-600 mb-1" />
                <span className="text-xs font-bold text-[#0A2918] uppercase">Water</span>
                <span className="text-sm text-[#2A4D3B] mt-1">{result.water}</span>
              </div>
            )}
            {result.soil && (
              <div className="bg-[#F2F7F4] border-2 border-[#0A2918] rounded-xl p-3 flex flex-col items-center text-center hard-shadow-sm">
                <Mountain size={24} strokeWidth={2.5} className="text-amber-700 mb-1" />
                <span className="text-xs font-bold text-[#0A2918] uppercase">Soil</span>
                <span className="text-sm text-[#2A4D3B] mt-1">{result.soil}</span>
              </div>
            )}
          </div>

          {/* Tips */}
          {result.tips && (
            <div className="bg-[#E25F45] text-white rounded-2xl p-4 hard-shadow-accent">
              <p className="text-sm font-bold uppercase tracking-wider mb-1">Pro Tip</p>
              <p className="text-base leading-relaxed">{result.tips}</p>
            </div>
          )}

          {/* Suggested Products */}
          {result.suggested_products?.length > 0 && (
            <div className="bg-white border-2 border-[#0A2918] rounded-2xl p-5 hard-shadow" data-testid="suggested-products">
              <h3 className="text-lg font-bold text-[#0A2918] mb-3" style={{ fontFamily: 'Work Sans' }}>
                Suggested Products
              </h3>
              <div className="space-y-2">
                {result.suggested_products.map((product, i) => (
                  <div key={i} className="flex items-center justify-between bg-[#F2F7F4] rounded-xl p-3 border border-[#0A2918]/20">
                    <span className="text-base font-medium text-[#0A2918]">{product}</span>
                    <button
                      onClick={() => handleAddToList(product)}
                      data-testid={`add-product-${i}`}
                      className="w-10 h-10 rounded-lg bg-[#0A2918] text-white flex items-center justify-center press-active"
                    >
                      <ShoppingCart size={18} strokeWidth={2.5} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              onClick={handleSave}
              disabled={saved}
              data-testid="save-plant-btn"
              className={`flex-1 h-14 text-lg font-bold rounded-xl press-active hard-shadow ${
                saved ? "bg-green-600" : "bg-[#28593A]"
              } text-white`}
            >
              <Save size={20} strokeWidth={2.5} className="mr-2" />
              {saved ? "Saved!" : "Save Plant"}
            </Button>
            <Button
              onClick={() => { setImagePreview(null); setImageBase64(null); setResult(null); setSaved(false); }}
              variant="outline"
              data-testid="new-scan-btn"
              className="flex-1 h-14 text-lg font-bold border-2 border-[#0A2918] rounded-xl press-active hard-shadow-sm"
            >
              <Camera size={20} strokeWidth={2.5} className="mr-2" /> New Scan
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
